import { useQuery } from "@apollo/client";
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import {
  GetComplianceProgramQuery,
  GetComplianceProgramQueryVariables,
} from "types/graphql-schema";

import Loading from "@components/loading/loading";
import usePkParams from "@helpers/hooks/use-pk-params";

import getComplianceProgramQuery from "./graphql/get-compliance-program-query";

/**
 * Programs no longer has its own page — the programs list, detail, and form now
 * live inside the Reviews (`/assessments`) and Surveys (`/survey`) apps. Legacy
 * `/programs/*` URLs (bookmarks, deep links from Reporting/calibration, older
 * notifications) are redirected into the correct section based on the program's
 * type: a survey program goes to `/survey/program/...`, everything else to
 * `/assessments/program/...`. The rest of the path and query string are
 * preserved so deep links (e.g. `/programs/5/reporting/manager?x=1`) keep
 * working.
 */
const ProgramRedirect = () => {
  const complianceProgramId = usePkParams("complianceProgramId");
  const location = useLocation();
  const { data, loading } = useQuery<
    GetComplianceProgramQuery,
    GetComplianceProgramQueryVariables
  >(getComplianceProgramQuery, {
    variables: { complianceProgramId },
  });

  if (loading) {
    return <Loading />;
  }

  const isSurvey = !!data?.complianceProgram?.engagementSurveyTemplate;
  const section = isSurvey ? "/survey/program" : "/assessments/program";
  // Strip the legacy `/programs/:id` prefix, keeping any trailing sub-path.
  const suffix = location.pathname.replace(/^\/programs\/\d+/, "");
  return (
    <Redirect
      to={`${section}/${complianceProgramId}${suffix}${location.search}`}
    />
  );
};

// Renders nothing but redirects, so it sets no document title: the page it
// lands on sets its own.
const Programs = () => {
  const { path } = useRouteMatch();
  const location = useLocation();

  return (
    <Switch>
      <Route exact path={`${path}/new`}>
        <Redirect
          to={
            new URLSearchParams(location.search).get("type") === "survey"
              ? "/survey/program/new"
              : "/assessments/program/new"
          }
        />
      </Route>
      <Route path={`${path}/:complianceProgramId(\\d+)`}>
        <ProgramRedirect />
      </Route>
      <Route>
        <Redirect to="/assessments/all" />
      </Route>
    </Switch>
  );
};

export default Programs;
